import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery } from '@tanstack/react-query';
import type { AuditAction, AuditLogRow } from 'shared';
import { api } from '../api';
import { Note } from '../components/Note';
import { useAuth } from '../auth';
import { formatDateTime } from '../format';
import { MONO, tokens } from '../theme';

/**
 * Nhật ký kiểm toán — spec SC-12 §5.
 *
 * Màn này tồn tại để trả lời bốn câu hỏi khi có sự cố: ai xoá trang đó, vì sao
 * điểm trang này tụt, ai đang bị dò mật khẩu, ai kích lượt quét cả danh sách lúc
 * 2 giờ sáng. Bộ lọc và cách trình bày bám theo đúng bốn câu đó.
 */

/**
 * Nhãn khai lại ở FE — `shared` biên dịch CommonJS nên chỉ import KIỂU được
 * (cùng lý do với `flow-steps.ts`).
 */
const NHAN: Record<AuditAction, string> = {
  'auth.login.success': 'Đăng nhập',
  'auth.login.failed': 'Đăng nhập thất bại',
  'auth.login.locked': 'Đăng nhập vào tài khoản đang khoá',
  'auth.logout': 'Đăng xuất',
  'auth.password.changed': 'Tự đổi mật khẩu',
  'user.created': 'Tạo tài khoản',
  'user.updated': 'Sửa tài khoản',
  'user.password.reset': 'Đặt lại mật khẩu hộ',
  'page.created': 'Thêm trang',
  'page.renamed': 'Đổi tên trang',
  'page.deleted': 'Xoá trang',
  'page.restored': 'Khôi phục trang',
  'page.owner.changed': 'Chuyển người phụ trách',
  'page.invariants.changed': 'Đổi luật bất biến',
  'page.weekly-rescan.changed': 'Bật/tắt quét hàng tuần',
  'scan.one': 'Quét một trang',
  'scan.all': 'Quét cả danh sách',
  'speed.measured': 'Đo tốc độ thật',
  'setting.changed': 'Đổi thiết lập hệ thống',
};

/** Hành động đáng chú ý thì tô màu, phần còn lại để trung tính cho đỡ nhiễu. */
const MAU: Partial<Record<AuditAction, { bg: string; fg: string }>> = {
  'auth.login.failed': { bg: tokens.amberBg, fg: tokens.amberText },
  'auth.login.locked': { bg: tokens.roseBg, fg: tokens.roseText },
  // Xoá trang tô đỏ; xoá VĨNH VIỄN thì `detail.vinhVien = true` — người trực đọc
  // cột Chi tiết sẽ thấy, không cần thêm một màu thứ tư cho cùng một hành động.
  'page.deleted': { bg: tokens.roseBg, fg: tokens.roseText },
  'user.password.reset': { bg: tokens.amberBg, fg: tokens.amberText },
};

/**
 * Chi tiết dạng JSON → một dòng người đọc được.
 *
 * Cố ý KHÔNG đổ nguyên JSON ra bảng: người trực đang tìm một chi tiết cụ thể
 * (vai trò cũ là gì, mất bao nhiêu lần quét), không phải đọc cấu trúc dữ liệu.
 */
function moTaChiTiet(r: AuditLogRow): string {
  const d = r.detail;
  const doi = (v: unknown): string => {
    if (v && typeof v === 'object' && 'cu' in v && 'moi' in v) {
      const o = v as { cu: unknown; moi: unknown };
      return `${JSON.stringify(o.cu)} → ${JSON.stringify(o.moi)}`;
    }
    return JSON.stringify(v);
  };

  const phan = Object.entries(d)
    .filter(([k]) => k !== 'email')
    .map(([k, v]) => `${k}: ${doi(v)}`);

  if (typeof d.email === 'string') phan.unshift(d.email);
  // Hành động không kèm chi tiết (đăng nhập, đăng xuất) thì hiện gạch ngang.
  // Để nguyên `{}` trông như dữ liệu bị lỗi chứ không như "không có gì để nói".
  return phan.length > 0 ? phan.join(' · ') : '—';
}

export function AuditPage() {
  const { user } = useAuth();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [action, setAction] = useState<AuditAction | ''>('');
  const [pageSlug, setPageSlug] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);

  const filter = {
    page,
    pageSize,
    ...(action ? { action } : {}),
    ...(pageSlug.trim() ? { pageSlug: pageSlug.trim() } : {}),
    ...(failedOnly ? { failedLoginsOnly: true } : {}),
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit', filter],
    queryFn: () => api.audit(filter),
    enabled: user?.role === 'WEB_ADMINISTRATOR',
  });

  if (user?.role !== 'WEB_ADMINISTRATOR') {
    return <Note tone="bad">Chỉ Web Administrator xem được nhật ký kiểm toán.</Note>;
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, letterSpacing: '.14em', color: tokens.teal, fontWeight: 600 }}>
        NHẬT KÝ KIỂM TOÁN
      </Typography>
      <Typography variant="h5" sx={{ fontSize: 28, color: tokens.ink, mt: 0.5 }}>
        Ai đã làm gì
      </Typography>
      <Typography sx={{ color: 'text.secondary', maxWidth: '70ch', mt: 0.5, mb: 3 }}>
        Chỉ ghi thêm, không sửa và không xoá được. Bản ghi thường giữ 12 tháng, bản ghi liên quan
        bảo mật giữ 24 tháng.
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.2}
          alignItems={{ md: 'center' }}
          sx={{ mb: 2 }}
          flexWrap="wrap"
          useFlexGap
        >
          {/* Ô tìm nhanh đứng đầu: đây là thứ người trực bấm khi nghi có người dò
              mật khẩu, và lúc đó họ đang vội. */}
          <Button
            size="small"
            variant={failedOnly ? 'contained' : 'outlined'}
            color={failedOnly ? 'error' : 'inherit'}
            onClick={() => {
              setFailedOnly(!failedOnly);
              setPage(1);
            }}
          >
            Đăng nhập thất bại 24h qua
          </Button>

          <TextField
            select
            size="small"
            label="Hành động"
            value={action}
            onChange={(e) => {
              setAction(e.target.value as AuditAction | '');
              setPage(1);
            }}
            sx={{ minWidth: 220 }}
            disabled={failedOnly}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {(Object.keys(NHAN) as AuditAction[]).map((a) => (
              <MenuItem key={a} value={a} sx={{ fontSize: 14 }}>
                {NHAN[a]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Trang (slug)"
            value={pageSlug}
            onChange={(e) => {
              setPageSlug(e.target.value);
              setPage(1);
            }}
            sx={{ width: 200 }}
          />

          <Box flex={1} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            component="a"
            href={api.auditCsvUrl(filter)}
          >
            Xuất CSV
          </Button>
        </Stack>

        {error && <Note tone="bad">{(error as Error).message}</Note>}

        {isLoading ? (
          <Stack spacing={1}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} variant="rounded" height={40} />
            ))}
          </Stack>
        ) : (
          <>
            <TableContainer sx={{ overflow: 'visible' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 152 }}>Thời điểm</TableCell>
                    <TableCell sx={{ width: 190 }}>Hành động</TableCell>
                    <TableCell sx={{ width: 165 }}>Người thực hiện</TableCell>
                    <TableCell sx={{ width: 120 }}>Trang</TableCell>
                    <TableCell>Chi tiết</TableCell>
                    <TableCell sx={{ width: 125 }}>IP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.rows.map((r) => {
                    const mau = MAU[r.action];
                    return (
                      <TableRow
                        key={r.id}
                        sx={{ '&:nth-of-type(even) td': { bgcolor: tokens.rowAlt } }}
                      >
                        <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                          {formatDateTime(r.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={NHAN[r.action] ?? r.action}
                            sx={{
                              height: 21,
                              fontSize: 12,
                              bgcolor: mau?.bg ?? tokens.line2,
                              color: mau?.fg ?? tokens.muted,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.actorName ? (
                            <Tooltip title={r.actorEmail ?? ''}>
                              <span>{r.actorName}</span>
                            </Tooltip>
                          ) : (
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              — chưa đăng nhập
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, fontSize: 12.5 }}>
                          {r.pageSlug ?? '—'}
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={JSON.stringify(r.detail)}
                        >
                          {moTaChiTiet(r)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, fontSize: 12 }}>
                          <Tooltip title={r.userAgent ?? ''}>
                            <span>{r.ip ?? '—'}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {data?.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Không có bản ghi nào khớp bộ lọc
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Phân trang PHÍA SERVER: bảng này lớn nhanh nhất hệ thống, kéo hết về
                trình duyệt là hỏng ngay trong tháng đầu. */}
            <TablePagination
              component="div"
              count={data?.total ?? 0}
              page={page - 1}
              onPageChange={(_, p) => setPage(p + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Số dòng mỗi trang"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} trong ${count}`}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
